import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import map from 'lodash/map';
import SubstanceDruglikenessComponent from '@Components/Results/substances/substance-druglikeness.component';
import AccordionHeader from '@Components/Results/common/AccordionHeader';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import SubstanceIdentificationData from '@Components/Results/substances/SubstanceIdentificationData';
import SubstanceSubProperty from '@Components/Results/substances/SubstanceSubProperty';
import SubstanceBioData from '@Components/Results/substances/substance-bio-data.component';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getFactCount } from '@Components/Results/substances/utils/factCountHelper';
import Catalyst from '@Services/Catalyst';
import Slugify from '@Utils/Slugify';

import './SubstanceProperty.scss';

const childView = resultsConfig.INITIAL_ITEMS_AMOUNT.substanceProperty.children;

function SubstanceProperty({ metadata, substance, fact, details, database, substanceIndex, toggle, zoom }) {
  const [childLimit, setChildLimit] = useState(childView);

  const id = useMemo(() => `accordion-${Slugify(fact.title)}-${substanceIndex}`, [fact.title, substanceIndex]);

  const toggleAccordion = () => {
    const { id: factId, open, title } = fact;

    if (!open) {
      if (metadata.isViewPreview) {
        Catalyst.trackContentLinkClick(title, `query:preview:${contextFromMetadata(metadata)}`);
      } else {
        Catalyst.trackContentLinkClick(title);
      }
      Spinners.activate(id);
      toggle(factId).finally(() => Spinners.terminate(id));
    } else {
      toggle(factId);
    }
  };

  const showMore = () => setChildLimit((limit) => limit + childView);

  const renderShowMoreButton = () => (
    <div className="els-btn-group-center u-els-margin-bottom-1o2">
      <button type="button" className="els-btn els-btn-sm els-btn-tertiary e2e-load-more" onClick={showMore}>
        <svg className="els-btn-icon">
          <use xlinkHref="#icon-plus" />
        </svg>
        <span>Load More</span>
      </button>
    </div>
  );

  const { children = [], className, title, type, id: factId, open, drugData } = fact;
  const count = getFactCount(fact, substance);

  return (
    <li className={classnames(['substance-accordion-item', { 'no-nested-data': type === 'calc' }, { closed: !open }])}>
      <AccordionHeader className={className} accordionId={id} isOpen={open} toggle={toggleAccordion}>
        {title} {count ? <span className="countLabel">- {count}</span> : null}
      </AccordionHeader>

      <RxSpinnerComponent spinnerId={id} position="relative" />

      {open && (
        <div id={id} className="rx-accordion-content u-els-padding-top-1x">
          {type !== 'category' && type !== 'calc' && (
            <ul className="rx-accordion small">
              {factId === 'ID' && (
                <li>
                  <SubstanceIdentificationData metadata={metadata} details={details} database={database} zoom={zoom} />
                </li>
              )}
              {map(
                children.slice(0, childLimit),
                (child, index) =>
                  child.id !== 'SEQ' && (
                    <SubstanceSubProperty
                      substance={substance}
                      details={details}
                      key={index}
                      metadata={metadata}
                      child={child}
                      fact={fact}
                      substanceIndex={substanceIndex}
                      database={database}
                      substanceId={details.id}
                      isDefaultOpen={fact.id === 'PRODD'}
                    />
                  )
              )}
              {children.length > childLimit && renderShowMoreButton()}
            </ul>
          )}

          {type === 'calc' && drugData && <SubstanceDruglikenessComponent drugData={drugData} />}

          {metadata.heatmapMode && type === 'category' && <SubstanceBioData metadata={metadata} fact={fact} substanceId={details.id} />}
        </div>
      )}
    </li>
  );
}

SubstanceProperty.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  fact: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
  substanceIndex: PropTypes.string.isRequired,
  toggle: PropTypes.func.isRequired,
  zoom: PropTypes.number,
};

export default SubstanceProperty;
